<template>
  <base-wrapper
    :component="VCard"
    v-bind="mergedAttrs"
    v-on="$listeners"
  >
    <slot />

    <template
      #default
    >
      <slot
        name="default"
      />
    </template>

    <!--    slot for custom progress linear (displayed when loading prop is not equal to Boolean False)-->
    <template
      #progress
    >
      <slot
        name="progress"
      />
    </template>
  </base-wrapper>
</template>

<script>
import BaseWrapper from './BaseWrapper.vue'
import { VCard } from 'vuetify/lib/components'

export default {
  name: 'VCardWrapper',

  components: {
    BaseWrapper
  },

  props: {
    /**
     * Configure the active CSS class applied when the link is active. You can find more information about the active-class prop on the vue-router documentation.
     */
    activeClass: {
      type: String,
      default: undefined
    },
    /**
     * Setting append prop always appends the relative path to the current path. You can find more information about the append prop on the vue-router documentation.
     */
    append: {
      type: Boolean,
      default: false
    },
    /**
     * Applies specified color to the control - it can be the name of material color (for example success or purple) or CSS color (#033 or rgba(255, 0, 0, 0.5)). You can find a list of built-in classes on the colors page.
     */
    color: {
      type: String,
      default: undefined
    },
    /**
     * Applies the dark theme variant to the component. You can find more information on the Material Design documentation for dark themes.
     */
    dark: {
      type: Boolean,
      default: false
    },
    /**
     * Removes the ability to click or target the component.
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Designates an elevation applied to the component between 0 and 24. You can find more information on the elevation page.
     */
    elevation: {
      type: [Number, String],
      default: undefined
    },
    /**
     * Exactly match the link. Without this, ‘/’ will match every route. You can find more information about the exact prop on the vue-router documentation.
     */
    exact: {
      type: Boolean,
      default: false
    },
    /**
     * Configure the active CSS class applied when the link is active with exact match. You can find more information about the exact-active-class prop on the vue-router documentation.
     */
    exactActiveClass: {
      type: String,
      default: undefined
    },
    /**
     * Configure the active CSS class applied when the link is active with exact match. You can find more information about the exact-active-class prop on the vue-router documentation.
     */
    exactPath: {
      type: Boolean,
      default: false
    },
    /**
     * Removes the card’s elevation.
     */
    flat: {
      type: Boolean,
      default: false
    },
    /**
     * Sets the height for the component.
     */
    height: {
      type: [Number, String],
      default: undefined
    },
    /**
     * Will apply an elevation of 4dp when hovered (default 2dp). You can find more information on the elevation page.
     */
    hover: {
      type: Boolean,
      default: false
    },
    /**
     * Designates the component as anchor and applies the href attribute.
     */
    href: {
      type: [String, Object],
      default: undefined
    },
    /**
     * Specifies an image background for the card. For more advanced implementations, it is recommended that you use the v-img component. You can find a v-img example here.
     */
    img: {
      type: String,
      default: undefined
    },
    /**
     * Applies the light theme variant to the component.
     */
    light: {
      type: Boolean,
      default: false
    },
    /**
     * Designates that the component is a link. This is automatic when using the href or to prop.
     */
    link: {
      type: Boolean,
      default: false
    },
    /**
     * Specifies the height of the loader.
     */
    loaderHeight: {
      type: [Number, String],
      default: 4
    },
    /**
     * Displays linear progress bar. Can either be a String which specifies which color is applied to the progress bar (any material color or theme color - primary, secondary, success, info, warning, error) or a Boolean which uses the component color (set by color prop - if it’s supported by the component) or the primary color.
     */
    loading: {
      type: [Boolean, String],
      default: false
    },
    /**
     * Sets the maximum height for the component.
     */
    maxHeight: {
      type: [Number, String],
      default: undefined
    },
    /**
     * Sets the maximum width for the component.
     */
    maxWidth: {
      type: [Number, String],
      default: undefined
    },
    /**
     * Sets the minimum height for the component.
     */
    minHeight: {
      type: [Number, String],
      default: undefined
    },
    /**
     * Sets the minimum width for the component.
     */
    minWidth: {
      type: [Number, String],
      default: undefined
    },
    /**
     * Specifies the link is a nuxt-link. For use with the nuxt framework.
     */
    nuxt: {
      type: Boolean,
      default: false
    },
    /**
     * Removes elevation (box-shadow) and adds a thin border.
     */
    outlined: {
      type: Boolean,
      default: false
    },
    /**
     * Specifies a higher default elevation (8dp). You can find more information on the elevation page.
     */
    raised: {
      type: Boolean,
      default: false
    },
    /**
     * Setting replace prop will call router.replace() instead of router.push() when clicked, so the navigation will not leave a history record. You can find more information about the 'replace' prop on the vue-router documentation.
     */
    replace: {
      type: Boolean,
      default: false
    },
    /**
     * Applies the v-ripple directive.
     */
    ripple: {
      type: [Boolean, Object],
      default: undefined
    },
    /**
     * Designates the border-radius applied to the component. You can find more information on the Border Radius page.
     */
    rounded: {
      type: [Boolean, String],
      default: undefined
    },
    /**
     * Applies a large border radius on the top left and bottom right of the card.
     */
    shaped: {
      type: Boolean,
      default: false
    },
    /**
     * Specify a custom tag used on the root element.
     */
    tag: {
      type: String,
      default: 'div'
    },
    /**
     * Designates the target attribute. This should only be applied when using the href prop.
     */
    target: {
      type: String,
      default: undefined
    },
    /**
     * Removes the component’s border-radius.
     */
    tile: {
      type: Boolean,
      default: false
    },
    /**
     * Denotes the target route of the link. You can find more information about the to prop on the vue-router documentation.
     */
    to: {
      type: [String, Object],
      default: undefined
    },
    /**
     * Sets the width for the component.
     */
    width: {
      type: [Number, String],
      default: undefined
    }
  },

  computed: {
    VCard () {
      return VCard
    },

    mergedAttrs () {
      return {
        ...this.$props,
        ...this.$attrs
      }
    }
  }
}
</script>
